import { useRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useTheme } from '@emotion/react'
import { Container } from 'chaoskit/src/components'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import { useViewportScroll, useTransform } from 'framer-motion'
import { text } from 'chaoskit/src/assets/styles/utility'

import SocialWidget from './SocialWidget'

import { StylesHeaderVariables } from '~partials/header/UI'
import Data from '~components/Data'
import Link from '~components/Link'
import Image from '~components/Image'

const TitleComponent = ({ hasLink, url, ...rest }) =>
  hasLink ? <Link to={url} {...rest} /> : <span {...rest} />

TitleComponent.propTypes = {
  hasLink: PropTypes.bool,
  url: PropTypes.string.isRequired,
}

const PostHero = ({
  className,
  currentPageUrl,
  date,
  dateTime,
  excerpt,
  featuredImage,
  hasLink = false,
  hasExcerpt = false,
  isHero = false,
  timeToRead,
  title,
  fullUrl,
  ...rest
}) => {
  const theme = useTheme()
  const heroRef = useRef()
  const featuredImageRef = useRef()

  const { scrollY } = useViewportScroll()

  const y = useTransform(scrollY, [0, 600], ['0%', '25%'])

  return (
    <section
      ref={heroRef}
      className={clsx(className, theme.settings.classes.contrast)}
      css={{
        position: 'relative',
        backgroundColor: theme.color.dark.base,
        paddingTop: isHero
          ? StylesHeaderVariables(theme).topOffset
          : theme.space.large,
        paddingBottom: theme.space.large,
        display: 'grid',
        alignItems: 'center',
        overflow: 'hidden',

        '&::after': {
          content: "''",
          position: 'absolute',
          zIndex: 1,
          background: `linear-gradient(to right, ${theme.color.dark.base}, rgba(0, 0, 0, 0))`,
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
        },

        [theme.mq.medium]: {
          minHeight: 600,
        },
      }}
      {...rest}
    >
      <Image
        ref={featuredImageRef}
        src={featuredImage}
        css={{
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}
        initial={isHero ? { y: 0 } : false}
        style={{ y: isHero ? y : 0 }}
      />
      <Container
        css={{
          position: 'relative',
          zIndex: 2,
          filter: `drop-shadow(0 1px 1px ${theme.color.dark.base})`,
        }}
      >
        <div
          css={{
            [theme.mq.medium]: {
              display: 'grid',
              gridTemplateColumns: '0.75fr',
            },
          }}
        >
          <div
            css={{
              ...text.expanded(theme),
              ...theme.text.medium,
              color: theme.color.primary.base,
              fontWeight: theme.fontWeight.bold,
            }}
          >
            {dateTime ? (
              <time itemProp="datePublished" dateTime={dateTime}>
                {date}
              </time>
            ) : (
              date
            )}{' '}
            - {timeToRead} min read
          </div>
          <h1 css={{ margin: 0, marginBottom: theme.space.small }}>
            <TitleComponent hasLink={hasLink} url={fullUrl}>
              {title}
            </TitleComponent>
          </h1>
          {hasExcerpt && <Data>{excerpt}</Data>}
          <div
            css={{
              height: 0,
              width: 300,
              borderImageSlice: 1,
              borderImageSource: generateGradient({
                start: theme.brand.teal,
                stop: theme.brand.green,
              }),
              borderStyle: 'solid',
              borderWidth: 0,
              borderTopWidth: 2,
              marginTop: theme.space.base,
              marginBottom: theme.space.medium,
            }}
          />
          <SocialWidget
            currentPageUrl={currentPageUrl}
            title={title}
            contrast
          />
        </div>
      </Container>
    </section>
  )
}

PostHero.propTypes = {
  className: PropTypes.string,
  currentPageUrl: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  dateTime: PropTypes.string,
  excerpt: PropTypes.string,
  featuredImage: PropTypes.string.isRequired,
  hasLink: PropTypes.bool,
  hasExcerpt: PropTypes.bool,
  /** Provides offset for top header */
  isHero: PropTypes.bool,
  timeToRead: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  fullUrl: PropTypes.string.isRequired,
}

export default PostHero
